import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import { authHrefConverter } from "./global-header.helpers";

import type { CurrentUser } from "../../data/user";
import type { GlobalHeaderSubmenu } from "./_submenu";

@customElement("bp-global-header-user-menu")
export class GlobalHeaderUserMenu extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      margin-inline: var(--size-3);
    }

    li {
      border-bottom: 1px solid var(--color-neutral-20);
    }

    a {
      appearance: none;
      cursor: pointer;
      box-sizing: border-box;
      display: inline-flex;
      width: 100%;
      align-items: center;
      justify-content: flex-start;
      vertical-align: middle;
      gap: var(--size-1);
      background-color: transparent;
      color: currentColor;
      border: none;
      font-size: var(--font-size-3xl);
      font-weight: var(--font-weight-medium);
      font-family: var(--font-sans);
      line-height: var(--line-height-none);
      -webkit-user-select: none;
      user-select: none;
      text-decoration: none;
      white-space: nowrap;
      padding-block: var(--size-2);
      border-radius: var(--radius-md);
      transition: background-color var(--duration-short) var(--ease-in-out);
    }

    a:focus-visible {
      outline: var(--a11y-outline);
    }

    a:focus:not(:focus-visible) {
      outline: none;
    }

    @media (hover: hover) {
      a:hover {
        opacity: var(--button-opacity-hover);
      }
    }

    a:active {
      opacity: var(--button-opacity-active);
    }

    @media (min-width: 1024px) {
      ul {
        margin-inline: 0;
      }
      li {
        border: none;
      }

      a {
        padding: var(--size-1);
        font-size: var(--font-size-md);
        border-radius: var(--radius-sm);
      }
      @media (hover: hover) {
        a:hover {
          opacity: 1;
          background-color: var(--color-neutral-20);
        }
      }
      a:active {
        background-color: var(--color-neutral-10);
      }
    }
  `;

  @query("#user-submenu")
  $submenu?: GlobalHeaderSubmenu;

  @property({ attribute: "href-giving" })
  hrefGiving: string = "";

  @property({ attribute: "href-help-center" })
  hrefHelpCenter: string = "";

  @property({ attribute: "href-profile" })
  hrefProfile: string = "";

  @property({
    attribute: "href-sign-in",
    converter: authHrefConverter,
  })
  hrefSignIn: string = "";

  @property({
    attribute: "href-sign-out",
    converter: authHrefConverter,
  })
  hrefSignOut: string = "";

  @property({ attribute: "label-giving" })
  labelGiving: string = "";

  @property({ attribute: "label-help-center" })
  labelHelpCenter: string = "";

  @property({ attribute: "label-profile" })
  labelProfile: string = "";

  @property({ attribute: "label-sign-in" })
  labelSignIn: string = "";

  @property({ attribute: "label-sign-out" })
  labelSignOut: string = "";

  @state()
  userInfo: CurrentUser | null = null;

  render() {
    return html`
      <nav>
        <ul>
          ${this.userInfo
            ? html`
                <li>
                  <a href="${this.hrefProfile}"> ${this.labelProfile} </a>
                </li>

                ${this.hrefGiving
                  ? html` <li>
                      <a href="${this.hrefGiving}"> ${this.labelGiving} </a>
                    </li>`
                  : nothing}
              `
            : nothing}

          <li>
            <a href="${this.hrefHelpCenter}"> ${this.labelHelpCenter} </a>
          </li>

          <li>
            ${this.userInfo
              ? html` <a href="${this.hrefSignOut}"> ${this.labelSignOut} </a> `
              : html` <a href="${this.hrefSignIn}"> ${this.labelSignIn} </a> `}
          </li>
        </ul>
      </nav>
    `;
  }

  setUserInfo(info: CurrentUser | null) {
    this.userInfo = info;
  }
}
